<template>
  <div class="lg:!mx-0 flex">
    <div class="flex flex-col max-h-content px-6 pb-2.5 min-h-content space-y-2.5 w-screen">
      <!-- Header Section -->
      <div class="mt-2.5">
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('CostAtComplete.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('CostAtComplete.subTitle') }}
            </p>
          </div>

          <div class="flex items-center space-x-4">
            <a
              v-if="fetchDailyQuantitiesAction.is(States.COMPLETE)"
              :href="exportUrl"
              target="_blank"
              class="btn btn--secondary-blue flex items-center space-x-2"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <span>{{ $t('CostAtComplete.button.export') }}</span>
            </a>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto flex-1">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-2 border-b border-gray-200 flex justify-between sticky top-0 z-10 rounded-t-lg">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
</svg>
            </div>

            <h2 class="text-lg font-semibold text-gray-900">
              {{ $t('CostAtComplete.analysisTitle') }}
            </h2>
          </div>

          <div class="flex items-end justify-between space-x-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('CostAtComplete.label.fromDate') }}
              </label>

              <AppInput
                v-model="dailyQuantitiesForm.form.from_date"
                type="date"
                name="from_date"
                class="form-input"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('CostAtComplete.label.toDate') }}
              </label>

              <AppInput
                v-model="dailyQuantitiesForm.form.to_date"
                type="date"
                name="to_date"
                class="form-input"
              />
            </div>

            <button
              type="button"
              class="btn btn--primary-blue flex items-center space-x-2"
              @click="fetchDailyQuantities"
            >
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4"
                :class="{ 'animate-reverse-spin': fetchDailyQuantitiesAction.is(States.LOADING) }">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
</svg>

              <span>{{ $t('CostAtComplete.button.apply') }}</span>
            </button>
          </div>
        </div>

        <div v-if="fetchDailyQuantitiesAction.is(States.LOADING)">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div class="flex flex-col items-center justify-center">
              <AppSpinner class="w-8 h-8 text-blue-600 mb-4" />

              <p class="text-sm text-gray-600">
                {{ $t('CostAtComplete.loading') }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-else-if="wbsTotals.size === 0 && fetchDailyQuantitiesAction.is(States.COMPLETE)"
          class="bg-white rounded-lg shadow-sm border border-gray-200 p-12"
        >
          <div class="text-center">
            <div class="mx-auto h-12 w-12 text-gray-300 mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
</svg>
            </div>

            <h3 class="text-sm font-medium text-gray-900 mb-1">
              {{ $t('CostAtComplete.emptyState.noDataTitle') }}
            </h3>

            <p class="text-sm text-gray-500 mb-6">
              {{ $t('CostAtComplete.emptyState.noDataMessage') }}
            </p>
          </div>
        </div>

        <div
          v-else-if="fetchDailyQuantitiesAction.is(States.COMPLETE)"
          class="overflow-auto"
        >
          <WbsCodeList
            :wbs-codes="wbsCodes"
            :to-complete-totals="toCompleteTotals"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import WbsCodeList from '@/components/CostAtComplete/WbsCodeList.vue';
import { useFormData } from '@/composables/useFormData';
import { useProjectTypeRedirect } from '@/composables/useProjectTypeRedirect';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput } from '@/helpers';
import { safeDenominator } from '@/helpers';
import { t } from '@/i18n';
import { useDailyQuantitiesStore } from '@/store/dailyQuantities';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { route } from '@/ziggy-shim';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const wbsCodesStore = useWbsCodesStore();
const dailyQuantitiesStore = useDailyQuantitiesStore();
const fetchDailyQuantitiesAction = useStoreApiAction(dailyQuantitiesStore.toComplete);
const indexWbsCodesAction = useStoreApiAction(wbsCodesStore.index);

const dailyQuantitiesForm = useFormData({
  to_date: dateForDateInput(new Date()),
  from_date: undefined,
});

const wbsTotals = ref<Map<string, { totalHours: number; totalCost: number; totalQuantity: number; totalDays: number }>>(
  new Map(),
);

const wbsCodes = computed(() => wbsCodesStore.models.where('project_id', parseInt(props.projectId)).get());

const toCompleteTotals = computed(() => {
  const toCompleteMap = new Map<number, CostAtCompletion>();

  for(const [key, value] of wbsTotals.value) {
    const wbsCode = wbsCodesStore.models.find(parseInt(key));

    if(wbsCode) {
      const toDateUnitCost = value.totalCost / safeDenominator(value.totalQuantity);
      const toDateProductionRate = value.totalQuantity / safeDenominator(value.totalDays);
      const toDateEarnedAmount = value.totalQuantity * wbsCode.unitCost;
      const toCompleteQuantity = (wbsCode.final_quantity || wbsCode.tender_quantity) - value.totalQuantity;

      const toCompleteProductionRate = wbsCode.to_complete_production_rate || toDateProductionRate ||
        wbsCode.tender_production_rate;

      const toCompleteUnitCost = toDateUnitCost !== 0 ?
        (toDateUnitCost /
          safeDenominator(toCompleteProductionRate / safeDenominator(toDateProductionRate))) :
        (wbsCode.unitCost);

      const toCompleteCost = toCompleteUnitCost * toCompleteQuantity;
      const forecastCost = value.totalCost + toCompleteCost;
      const revenueQuantity = wbsCode.final_quantity || wbsCode.tender_quantity;

      const profit = (value.totalCost + toCompleteCost) === 0 ?
        0 :
        (revenueQuantity * wbsCode.unitCost) - (value.totalCost + toCompleteCost);

      toCompleteMap.set(wbsCode.id, {
        toDateQuantity: value.totalQuantity,
        toDateDays: (value.totalQuantity / toDateProductionRate) || 0,
        toDateProductionRate,
        toDateUnitCost,
        toDateCost: value.totalCost,
        toDateEarnedAmount,
        toDateVariance: toDateEarnedAmount - value.totalCost,
        toCompleteQuantity,
        toCompleteProductionRate,
        toCompleteUnitCost,
        toCompleteDays: toCompleteQuantity / safeDenominator(toCompleteProductionRate),
        toCompleteCost,
        forecastCost,
        profit,
      });
    }
  }

  return toCompleteMap;
});

const fetchDailyQuantities = () => {
  fetchDailyQuantitiesAction.request(props.projectId, dailyQuantitiesForm.form).then((data) => {
    wbsTotals.value = data;
  }).catch((error) => {
    Swal.fire({
      icon: 'error',
      title: t('CostAtComplete.error.serverErrorTitle'),
      text: t('CostAtComplete.error.serverErrorMessage'),
    });

    console.log(error);
  });
};

watch(() => props.projectId, (newValue) => {
  if(newValue) {
    fetchDailyQuantities();
    indexWbsCodesAction.request(props.projectId);
  }
}, { immediate: true });

const exportUrl = computed(() => {
  return `${
    route('api.v1.quantities.cost-at-complete-export', props.projectId)
  }?to_date=${dailyQuantitiesForm.form.to_date}`;
});

useProjectTypeRedirect();
</script>

<style scoped></style>
